import {
    LazyModule,
    createLazyComponent,
    registerLazyOrchestrator,
    type LazyComponent,
} from 'owa-bundling';
import type * as lazyIndex from './lazyIndex';
import type { CopilotSearchAnswerProps } from './components/CopilotSearchAnswer';
import { loadTableViewFromSearchTableQuery } from 'owa-mail-search';
import { endSearchSession } from 'owa-search-actions';

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule<typeof lazyIndex>(
    () => import(/* webpackChunkName: "CopilotSearchAnswer" */ './lazyIndex'),
    { name: 'CopilotSearchAnswer' }
);

// Lazy component for rendering in search results
export const LazyCopilotSearchAnswer: LazyComponent<{}, unknown, void> = createLazyComponent(
    lazyModule,
    (m: typeof lazyIndex) => m.CopilotSearchAnswer
);

// Lazy container that displays the copilot answer
export const LazyCopilotSearchAnswerContainer: LazyComponent<CopilotSearchAnswerProps, void> =
    createLazyComponent(lazyModule, (m: typeof lazyIndex) => m.CopilotSearchAnswerContainer);

// @1js/scc-react-markdown-renderer (loaded via lazyIndex) constructs regexes with negative
// lookbehind at module-eval time, which throws on Safari < 16.4 and fails the chunk load.
// Feature-test here rather than UA-sniff because isBrowserSafari/isMinimumBrowserVersion are
// blocked at module scope by owa-custom-rules/no-side-effects.
// TODO: Remove this gate once @1js/scc-react-markdown-renderer drops the lookbehind regexes.
// Tracked in https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/429095
let supportsRegexLookbehind = false;
try {
    new RegExp('(?<!a)b');
    supportsRegexLookbehind = true;
} catch {
    // Lookbehind unsupported (Safari < 16.4); leave supportsRegexLookbehind = false.
}

if (supportsRegexLookbehind) {
    registerLazyOrchestrator(
        loadTableViewFromSearchTableQuery,
        lazyModule,
        m => m.startCopilotSearchOrchestrator
    );

    registerLazyOrchestrator(endSearchSession, lazyModule, m => m.clearCopilotSearchOrchestrator);
}

// Re-export selectors for checking state
export { getCopilotSearchAnswer, getIsLoading, getIsScrollMinimized } from './selectors/selectors';

// Re-export mutators that need to be called from outside the lazy chunk
export { setScrollMinimizedMutator } from './mutators/mutators';
